import { useState, useEffect, useRef, useCallback } from 'react';
import { apiClient } from '../../api/client';
import type { DashboardInventory, OccupancyZone, CheckInCount, DashboardState } from './types';

interface UnassignedGuest {
  id: string;
  fullName: string;
}

const REFRESH_INTERVAL_MS = 12_000;

export function LiveDashboardPanel() {
  const [state, setState] = useState<DashboardState>({
    inventory: null,
    occupancy: [],
    checkInCount: null,
    lastRefreshed: null,
    isStale: false,
    isLoading: true,
  });
  const [unassignedCount, setUnassignedCount] = useState<number>(0);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchDashboardData = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      const [inventory, occupancy, checkInCount, unassigned] = await Promise.all([
        apiClient<DashboardInventory>('/table-management/inventory'),
        apiClient<OccupancyZone[]>('/table-management/reports/occupancy'),
        apiClient<CheckInCount>('/table-management/check-in/count'),
        apiClient<UnassignedGuest[]>('/table-management/reports/unassigned').catch(() => []),
      ]);

      setState({
        inventory,
        occupancy,
        checkInCount,
        lastRefreshed: new Date(),
        isStale: false,
        isLoading: false,
      });
      setUnassignedCount(Array.isArray(unassigned) ? unassigned.length : 0);
    } catch {
      setState((prev) => ({
        ...prev,
        isStale: true,
        isLoading: false,
      }));
    }
  }, []);

  const startInterval = useCallback(() => {
    intervalRef.current = setInterval(() => {
      fetchDashboardData();
    }, REFRESH_INTERVAL_MS);
  }, [fetchDashboardData]);

  const clearIntervalRef = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Mount: fetch immediately and start interval
  useEffect(() => {
    fetchDashboardData();
    startInterval();

    return () => {
      clearIntervalRef();
    };
  }, [fetchDashboardData, startInterval, clearIntervalRef]);

  const handleManualRefresh = () => {
    clearIntervalRef();
    fetchDashboardData();
    startInterval();
  };

  const checkInPercent =
    state.checkInCount && state.checkInCount.totalAssigned > 0
      ? Math.round((state.checkInCount.checkedIn / state.checkInCount.totalAssigned) * 100)
      : 0;

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <h2 style={titleStyle}>Live Dashboard</h2>
        {state.isStale && state.lastRefreshed && (
          <div style={staleBadgeStyle}>
            ⚠️ Stale data — last updated {state.lastRefreshed.toLocaleTimeString()}
          </div>
        )}
      </div>

      {/* Manual Refresh Button */}
      <button
        onClick={handleManualRefresh}
        disabled={state.isLoading}
        style={refreshButtonStyle}
        aria-label="Refresh dashboard data"
      >
        {state.isLoading ? '⟳ Refreshing...' : '⟳ Refresh Now'}
      </button>

      {/* Check-In Progress */}
      {state.checkInCount && (
        <div style={sectionStyle}>
          <h3 style={sectionTitleStyle}>Check-In Progress</h3>
          <div style={progressContainerStyle}>
            <div style={{ ...progressBarFillStyle, width: `${checkInPercent}%` }} />
          </div>
          <div style={progressLabelStyle}>
            {state.checkInCount.checkedIn} / {state.checkInCount.totalAssigned} checked in ({checkInPercent}%)
          </div>
        </div>
      )}

      {/* Status Pills */}
      {state.checkInCount && (
        <div style={pillsRowStyle}>
          <div style={{ ...pillStyle, borderTopColor: '#f59e0b' }}>
            <div style={{ ...inventoryValueStyle, color: '#f59e0b' }}>
              {state.checkInCount.totalAssigned - state.checkInCount.checkedIn}
            </div>
            <div style={inventoryLabelStyle}>Awaiting Check-in</div>
          </div>
          <div style={{ ...pillStyle, borderTopColor: '#dc2626' }}>
            <div style={{ ...inventoryValueStyle, color: '#dc2626' }}>{unassignedCount}</div>
            <div style={inventoryLabelStyle}>No Seat Assigned</div>
          </div>
        </div>
      )}

      {/* Seat Inventory */}
      {state.inventory && (
        <div style={sectionStyle}>
          <h3 style={sectionTitleStyle}>Seat Inventory</h3>
          <div style={inventoryGridStyle}>
            <InventoryCard label="Total" value={state.inventory.overall.total} color="#333" />
            <InventoryCard label="Assigned" value={state.inventory.overall.assigned} color="#2563eb" />
            <InventoryCard label="Available" value={state.inventory.overall.available} color="#16a34a" />
            <InventoryCard label="Held" value={state.inventory.overall.held} color="#d97706" />
            <InventoryCard label="Blocked" value={state.inventory.overall.blocked} color="#dc2626" />
            <InventoryCard label="Checked In" value={state.inventory.overall.checkedIn} color="#7c3aed" />
          </div>
        </div>
      )}

      {/* Occupancy by Zone */}
      {state.occupancy.length > 0 && (
        <div style={sectionStyle}>
          <h3 style={sectionTitleStyle}>Occupancy by Zone</h3>
          <div style={zoneListStyle}>
            {state.occupancy.map((zone) => (
              <ZoneCard key={zone.zoneId} zone={zone} />
            ))}
          </div>
        </div>
      )}

      {/* Loading state for initial load */}
      {state.isLoading && !state.inventory && !state.checkInCount && (
        <div style={loadingStyle}>Loading dashboard data...</div>
      )}
    </div>
  );
}

function InventoryCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div style={{ ...inventoryCardStyle, borderTopColor: color }}>
      <div style={{ ...inventoryValueStyle, color }}>{value}</div>
      <div style={inventoryLabelStyle}>{label}</div>
    </div>
  );
}

function ZoneCard({ zone }: { zone: OccupancyZone }) {
  const occupancyColor =
    zone.occupancyPercent >= 90
      ? '#dc2626'
      : zone.occupancyPercent >= 70
        ? '#d97706'
        : '#16a34a';

  return (
    <div style={{ ...zoneCardStyle, borderLeftColor: occupancyColor }}>
      <div style={zoneNameStyle}>{zone.zoneName}</div>
      <div style={zoneStatsStyle}>
        {zone.occupiedSeats} / {zone.totalSeats} seats ({zone.occupancyPercent}%)
      </div>
      <div style={zoneBarContainerStyle}>
        <div style={{ ...zoneBarFillStyle, width: `${zone.occupancyPercent}%`, backgroundColor: occupancyColor }} />
      </div>
    </div>
  );
}

// --- Inline Styles ---

const containerStyle: React.CSSProperties = {
  padding: '16px',
  paddingBottom: '80px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '20px',
  fontWeight: 700,
  color: '#1a1a1a',
};

const staleBadgeStyle: React.CSSProperties = {
  padding: '8px 12px',
  backgroundColor: '#fef3c7',
  border: '1px solid #f59e0b',
  borderRadius: '8px',
  fontSize: '13px',
  color: '#92400e',
};

const refreshButtonStyle: React.CSSProperties = {
  width: '100%',
  minHeight: '52px',
  padding: '14px 24px',
  fontSize: '16px',
  fontWeight: 600,
  color: '#fff',
  backgroundColor: '#2563eb',
  border: 'none',
  borderRadius: '12px',
  cursor: 'pointer',
  touchAction: 'manipulation',
};

const sectionStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
};

const sectionTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '16px',
  fontWeight: 600,
  color: '#374151',
};

const progressContainerStyle: React.CSSProperties = {
  width: '100%',
  height: '20px',
  backgroundColor: '#e5e7eb',
  borderRadius: '10px',
  overflow: 'hidden',
};

const progressBarFillStyle: React.CSSProperties = {
  height: '100%',
  backgroundColor: '#16a34a',
  borderRadius: '10px',
  transition: 'width 0.3s ease',
};

const progressLabelStyle: React.CSSProperties = {
  fontSize: '14px',
  color: '#4b5563',
  textAlign: 'center',
};

const inventoryGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '10px',
};

const pillsRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: '10px',
};

const pillStyle: React.CSSProperties = {
  padding: '14px 12px',
  backgroundColor: '#f9fafb',
  borderRadius: '10px',
  borderTop: '3px solid',
  textAlign: 'center',
};

const inventoryCardStyle: React.CSSProperties = {
  padding: '12px',
  backgroundColor: '#f9fafb',
  borderRadius: '8px',
  borderTop: '3px solid',
  textAlign: 'center',
};

const inventoryValueStyle: React.CSSProperties = {
  fontSize: '22px',
  fontWeight: 700,
};

const inventoryLabelStyle: React.CSSProperties = {
  fontSize: '12px',
  color: '#6b7280',
  marginTop: '4px',
};

const zoneListStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
};

const zoneCardStyle: React.CSSProperties = {
  padding: '12px',
  backgroundColor: '#f9fafb',
  borderRadius: '8px',
  borderLeft: '4px solid',
};

const zoneNameStyle: React.CSSProperties = {
  fontSize: '14px',
  fontWeight: 600,
  color: '#1f2937',
};

const zoneStatsStyle: React.CSSProperties = {
  fontSize: '13px',
  color: '#6b7280',
  marginTop: '4px',
};

const zoneBarContainerStyle: React.CSSProperties = {
  width: '100%',
  height: '8px',
  backgroundColor: '#e5e7eb',
  borderRadius: '4px',
  overflow: 'hidden',
  marginTop: '6px',
};

const zoneBarFillStyle: React.CSSProperties = {
  height: '100%',
  borderRadius: '4px',
  transition: 'width 0.3s ease',
};

const loadingStyle: React.CSSProperties = {
  textAlign: 'center',
  padding: '32px 16px',
  fontSize: '14px',
  color: '#6b7280',
};
